import React, { useState } from 'react';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import useData from '../useData';
import useDirtyForm from '../useDirtyForm';
import PageShell, { Button } from '../../ui/PageShell';
import Table from '../../ui/Table';
import Modal from '../../ui/Modal';
import { FormGrid, Field, Select, Input } from '../../ui/Form';
import { sectionOptions } from '../../ui/labels';
import HelpButton from '../help/HelpButton';
import EmptyGuide from '../help/EmptyGuide';

/** Who teaches which subject in which section. Swapping a teacher is a
 *  single-row update plus a history entry, never a delete + recreate. */
export default function Assignments() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(async () => {
    const [assignments, sections, subjects, teachers] = await Promise.all([
      api.assignments(), api.sections(), api.subjects(), api.teachers(),
    ]);
    return { assignments, sections, subjects, teachers };
  }, []);

  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ section_id: '', subject_id: '', teacher_id: '' });
  const [saving, setSaving] = useState(false);
  const [swapping, setSwapping] = useState(null);
  const [newTeacher, setNewTeacher] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const dirty = useDirtyForm(form);
  const swapDirty = useDirtyForm({ newTeacher, reason });

  const sections = data?.sections || [];
  const subjects = data?.subjects || [];
  const teachers = data?.teachers || [];

  // Labels arrive on the rows from the server; no client-side joins.
  const rows = (data?.assignments || []).map((a) => ({
    ...a,
    section_title: a.section_label || '—',
    subject_title: a.subject_title || '—',
    teacher_name: a.teacher_name || '—',
  }));

  const openNew = () => {
    const next = {
      section_id: '', subject_id: '', teacher_id: '',
    };
    setForm(next);
    dirty.reset(next);
    setCreating(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.section_id || !form.subject_id || !form.teacher_id) return toast.error('جميع الحقول مطلوبة');
    setSaving(true);
    try {
      await api.createAssignment({
        section_id: Number(form.section_id),
        subject_id: Number(form.subject_id),
        teacher_id: Number(form.teacher_id),
      });
      toast.success('تم إنشاء التوكيل');
      setCreating(false);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const doSwap = async (e) => {
    e.preventDefault();
    if (!newTeacher) return toast.error('اختر المعلم الجديد');
    setBusy(true);
    try {
      await api.swapTeacher(swapping.id, Number(newTeacher), reason || undefined);
      toast.success('تم تبديل المعلم');
      setSwapping(null);
      setNewTeacher('');
      setReason('');
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <PageShell title="تدريس المواد" description="توكيل المعلمين بالمواد في كل شعبة"
      actions={(
        <>
          <HelpButton guides={['assign-teachers', 'primary-setup']} />
          <Button onClick={openNew} disabled={loading} data-guide="assignments-add">+ توكيل جديد</Button>
        </>
      )}>
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Table
        keyField="id"
        rows={rows}
        loading={loading}
        statusKey="status"
        emptyText={<EmptyGuide text="لا توجد توكيلات بعد" guide="assign-teachers" />}
        searchPlaceholder="ابحث بالمعلم أو المادة..."
        renderActions={(a) => a.status === 'active' && (
          <button type="button" className="ui-btn-mini ui-btn-mini-primary"
            onClick={() => { setNewTeacher(''); setReason(''); swapDirty.reset({ newTeacher: '', reason: '' }); setSwapping(a); }}>
            تبديل المعلم
          </button>
        )}
        columns={[
          { key: 'teacher_name', label: 'المعلم', width: 2, filterable: false, render: (r) => <strong>{r.teacher_name}</strong> },
          { key: 'subject_title', label: 'المادة' },
          { key: 'section_title', label: 'الشعبة', nowrap: true, align: 'center',
            render: (r) => <span className="ui-badge ui-badge-info">{r.section_title}</span> },
          { key: 'status', label: 'الحالة', nowrap: true, align: 'center',
            badge: (v) => (v === 'active' ? 'success' : 'neutral'),
            formatValue: (v) => (v === 'active' ? 'نشط' : 'منتهي') },
        ]}
      />

      {creating && (
        <Modal open size="md" title="توكيل جديد" onClose={() => setCreating(false)}
          confirmClose={dirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button variant="success" onClick={save} loading={saving} data-guide="modal-save">حفظ</Button>
            </>
          )}>
          <form onSubmit={save}>
            <FormGrid columns={2}>
              <Field label="الشعبة" required guide="assign-section">
                <Select value={form.section_id} onChange={set('section_id')} placeholder="اختر الشعبة"
                  options={sectionOptions(sections.filter((s) => s.status === 'active'))} />
              </Field>
              <Field label="المادة" required guide="assign-subject">
                <Select value={form.subject_id} onChange={set('subject_id')} placeholder="اختر المادة"
                  options={subjects.map((s) => ({ value: s.id, label: s.title }))} />
              </Field>
              <Field label="المعلم" required guide="assign-teacher">
                <Select value={form.teacher_id} onChange={set('teacher_id')} placeholder="اختر المعلم"
                  options={teachers.filter((t) => t.status === 'active').map((t) => ({ value: t.id, label: t.name }))} />
              </Field>
            </FormGrid>
          </form>
        </Modal>
      )}

      {swapping && (
        <Modal open size="sm" title="تبديل المعلم" onClose={() => setSwapping(null)}
          confirmClose={swapDirty.isDirty}
          footer={(requestClose) => (
            <>
              <Button variant="secondary" onClick={requestClose}>إلغاء</Button>
              <Button onClick={doSwap} loading={busy}>تبديل</Button>
            </>
          )}>
          <p className="ui-modal-message" style={{ marginBottom: 'var(--ui-space-3)' }}>
            {swapping.subject_title} — {swapping.section_title}
            <br />المعلم الحالي: <strong>{swapping.teacher_name}</strong>
          </p>
          <FormGrid columns={1}>
            <Field label="المعلم الجديد" required>
              <Select value={newTeacher} onChange={(e) => setNewTeacher(e.target.value)} placeholder="اختر المعلم"
                options={teachers.filter((t) => t.status === 'active' && t.id !== swapping.teacher_id)
                  .map((t) => ({ value: t.id, label: t.name }))} />
            </Field>
            <Field label="السبب" hint="يُحفظ في سجل التبديلات">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="مثال: انتقل لشعبة أخرى" />
            </Field>
          </FormGrid>
        </Modal>
      )}
    </PageShell>
  );
}
